import React, { useCallback, useState } from "react";
import { Button, InputNumber } from "antd";
import { CreditCardOutlined } from "@ant-design/icons";
import { Elements } from "@stripe/react-stripe-js";
import BasicModal from "../../ui/BasicModal/BasicModal";
import AddCardForm from "./AddCardForm";
import AuthService from "../../base/services/authentication.service";
import { fundPackages, BUY_STEP, PAYMENT_MODE, BONUS_LABEL, getBonusFunds } from "./constants";
import { formatAmount } from "./utils";
import { useBuyFunds } from "./useBuyFunds";

const CUSTOM_PACKAGE_ID = "custom";

const PackageStep = ({
   selectedPackage,
   onSelect,
   customFunds,
   onCustomChange,
   onCancel,
   onConfirm
}) => {
   const isCustom = selectedPackage?.id === CUSTOM_PACKAGE_ID;
   const bonusFunds = customFunds ? getBonusFunds(customFunds) : 0;
   const canConfirm = selectedPackage?.funds > 0;

   return (
      <>
         <p className="buy-funds__description">
            Funds are used to send surveys and process responses. Select an amount below and
            complete your payment to continue.
         </p>

         <div className="buy-funds__packages">
            {fundPackages.map((pkg) => (
               <button
                  key={pkg.id}
                  type="button"
                  className={`buy-funds__package${
                     selectedPackage?.id === pkg.id ? " buy-funds__package--selected" : ""
                  }`}
                  onClick={() => onSelect(pkg)}
               >
                  {pkg.bonus && <span className="buy-funds__package-bonus">{pkg.bonus}</span>}
                  <span className="buy-funds__package-funds">{formatAmount(pkg.funds)}</span>
               </button>
            ))}
         </div>

         <div className={`buy-funds__custom${isCustom ? " buy-funds__custom--selected" : ""}`}>
            <label className="buy-funds__custom-label" htmlFor="buy-funds-custom">
               Or enter a custom amount
            </label>
            <span className="buy-funds__custom-hint">Minimum amount is $1.</span>
            <div className="buy-funds__custom-row">
               <InputNumber
                  id="buy-funds-custom"
                  className="buy-funds__custom-input"
                  min={1}
                  precision={0}
                  prefix="$"
                  value={customFunds}
                  onChange={onCustomChange}
                  placeholder="Amount in USD"
               />
               {/* <span className="buy-funds__custom-price">
                  {customFunds ? formatAmount(customFunds) : "—"}
               </span> */}
            </div>
            {bonusFunds > 0 && (
               <span className="buy-funds__custom-bonus">
                  {BONUS_LABEL} — you'll receive {formatAmount(customFunds + bonusFunds)}
               </span>
            )}
         </div>

         {/* <p className="buy-funds__note">Prices may change over time.</p> */}

         <div className="buy-funds__actions">
            <Button className="defaultButton" onClick={onCancel}>
               Cancel
            </Button>
            <Button
               type="primary"
               className="primaryButton"
               disabled={!canConfirm}
               onClick={onConfirm}
            >
               Confirm
            </Button>
         </div>
      </>
   );
};

const SavedCardStep = ({ card, isBuying, isCardProcessing, onChangeCard, onCancel, onBuy }) => (
   <>
      <div className="buy-funds__method">
         <div className="buy-funds__method-header">
            <span className="buy-funds__method-title">Payment method</span>
            <Button type="link" className="buy-funds__change" onClick={onChangeCard}>
               Change
            </Button>
         </div>
         <div className="buy-funds__card">
            <div className="buy-funds__card-icon">
               <CreditCardOutlined />
            </div>
            <div className="buy-funds__card-info">
               <span className="buy-funds__card-name">{card.brand ?? "Card"}</span>
               <span className="buy-funds__card-digits">
                  {card.brand ?? "Card"} ending in ****{card.last4CardDigits}
               </span>
            </div>
         </div>
      </div>

      <div className="buy-funds__actions">
         <Button
            className="defaultButton"
            onClick={onCancel}
            disabled={isBuying || isCardProcessing}
         >
            Cancel
         </Button>
         <Button
            type="primary"
            className="primaryButton"
            loading={isBuying || isCardProcessing}
            disabled={isCardProcessing}
            onClick={onBuy}
         >
            Add funds
         </Button>
      </div>
   </>
);

const BuyFundsModal = ({ isOpen, onClose, savedCard, stripePromise, onPurchased }) => {
   const organizationId = AuthService.getOrganizationId();

   const [step, setStep] = useState(BUY_STEP.PACKAGE);
   const [selectedPackage, setSelectedPackage] = useState(fundPackages[0]);
   // Free-entry funds amount; null when the user is on a preset instead.
   const [customFunds, setCustomFunds] = useState(null);
   const [mode, setMode] = useState(
      savedCard?.paymentMethodId ? PAYMENT_MODE.SAVED : PAYMENT_MODE.NEW
   );
   const [isEditCardOpen, setIsEditCardOpen] = useState(false);

   // Picking a preset clears any custom entry; typing a custom amount builds a
   // one-off package so the rest of the flow (price, /pay) treats it uniformly.
   const handleSelectPackage = useCallback((pkg) => {
      setCustomFunds(null);
      setSelectedPackage(pkg);
   }, []);

   const handleCustomChange = useCallback((value) => {
      setCustomFunds(value);
      setSelectedPackage(value > 0 ? { id: CUSTOM_PACKAGE_ID, funds: value } : null);
   }, []);

   const activateSavedCard = useCallback(() => setMode(PAYMENT_MODE.SAVED), []);
   // The parent unmounts this modal on close, so closing alone resets all local
   // state — no explicit reset needed.
   const handlePurchaseSettled = useCallback(() => {
      onPurchased?.();
      onClose();
   }, [onPurchased, onClose]);

   const {
      card,
      isBuying,
      isCardProcessing,
      isPaymentInFlight,
      hasSavedCard,
      setIsCardProcessing,
      refreshSavedCard,
      handleCardAdded,
      buyWithSavedCard
   } = useBuyFunds({
      organizationId,
      initialCard: savedCard,
      stripePromise,
      onCardActivated: activateSavedCard,
      onPurchaseSettled: handlePurchaseSettled
   });

   const showSavedCard = step === BUY_STEP.PAYMENT && mode === PAYMENT_MODE.SAVED && hasSavedCard;

   // Block all close paths (X / ESC / mask) while a charge is in flight.
   const handleModalCancel = () => {
      if (isPaymentInFlight) return;
      onClose();
   };

   // Same guard for the edit-card modal: while the add-card API (and the
   // refresh that follows) is in flight, closing would unmount the form mid-call.
   const handleEditCardCancel = () => {
      if (isCardProcessing) return;
      setIsEditCardOpen(false);
   };

   return (
      <BasicModal isModalOpen={isOpen} handleCancel={handleModalCancel} className="buy-funds">
         <div className="buy-funds__content">
            <h2 className="buy-funds__title">Add funds</h2>

            {step === BUY_STEP.PACKAGE ? (
               <PackageStep
                  selectedPackage={selectedPackage}
                  onSelect={handleSelectPackage}
                  customFunds={customFunds}
                  onCustomChange={handleCustomChange}
                  onCancel={onClose}
                  onConfirm={() => setStep(BUY_STEP.PAYMENT)}
               />
            ) : showSavedCard ? (
               <SavedCardStep
                  card={card}
                  isBuying={isBuying}
                  isCardProcessing={isCardProcessing}
                  onChangeCard={() => setIsEditCardOpen(true)}
                  onCancel={onClose}
                  onBuy={() => buyWithSavedCard(selectedPackage)}
               />
            ) : (
               // No saved card: collect + save one. handleCardAdded re-fetches
               // it and flips to the saved-card view so the user can then buy.
               <Elements stripe={stripePromise}>
                  <AddCardForm
                     onCardSaved={handleCardAdded}
                     onProcessing={setIsCardProcessing}
                     processing={isCardProcessing}
                  />
               </Elements>
            )}
         </div>

         {isEditCardOpen && (
            <BasicModal isModalOpen={isEditCardOpen} handleCancel={handleEditCardCancel}>
               <Elements stripe={stripePromise}>
                  <AddCardForm
                     onClose={() => setIsEditCardOpen(false)}
                     onCardSaved={refreshSavedCard}
                     onProcessing={setIsCardProcessing}
                     processing={isCardProcessing}
                  />
               </Elements>
            </BasicModal>
         )}
      </BasicModal>
   );
};

export default BuyFundsModal;
